import type { Comment, CommentTargetType } from '@/@types/comment'
import { ADD_COMMENT } from '@/constants/api'
import useUser from '@/utils/hooks/useUser'
import { post } from '@/utils/request'
import { zodResolver } from '@hookform/resolvers/zod'
import { Avatar as MantineAvatar } from '@mantine/core'
import { MIME_TYPES } from '@mantine/dropzone'
import clsx from 'clsx'
import { first } from 'lodash-es'
import { useRef, type HTMLAttributes } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ZodError } from 'zod'
import AttachmentDropzone from '../Attachment/AttachmentDropzone'
import useAttachmentsHelper from '../Attachment/useAttachmentsHelper'
import Avatar from '../Avatar'
import type { LexicalEditorHandle } from '../Editor/LexicalEditor'
import type { CommentContentEditorProps } from './CommentContentEditor'
import CommentFormFields from './CommentFormFields'
import {
	CommentFormSchema,
	CreateCommentPayloadSchema,
	type CommentFormValue,
	type ReplyToComment,
} from './schema'
import styles from './CommentForm.module.css'

export interface CommentFormProps
	extends
		Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'>,
		Pick<CommentContentEditorProps, 'autoFocus' | 'placeholder'> {
	allowSyncToPersonalUpdate?: boolean
	onSubmit?: (data: Comment) => void
	replyToComment?: ReplyToComment | null
	submitLabel?: string
	targetId: string
	targetType: CommentTargetType
}

export default function CommentForm({
	allowSyncToPersonalUpdate = true,
	onSubmit,
	placeholder,
	replyToComment,
	submitLabel = '回复',
	targetId,
	targetType,
	autoFocus,
	className,
	...props
}: CommentFormProps) {
	const contentEditorRef = useRef<LexicalEditorHandle>(null)
	const user = useUser(void 0)
	const methods = useForm<CommentFormValue>({
		defaultValues: {
			content: '',
			attachment: { ids: [], map: {} },
			isSyncToPersonalUpdate: false,
		},
		resolver: zodResolver(CommentFormSchema),
		mode: 'onChange',
	})
	const attachmentHandlers = useAttachmentsHelper(methods)

	const handleSubmit = async (formValue: CommentFormValue) => {
		const payload = await CreateCommentPayloadSchema.parseAsync({
			...formValue,
			isSyncToPersonalUpdate: allowSyncToPersonalUpdate ? formValue.isSyncToPersonalUpdate : false,
			targetType,
			targetId,
			threadId: replyToComment?.threadId ?? undefined,
			replyToCommentId: replyToComment?.to?.id,
		}).catch((error) => {
			if (error instanceof ZodError) {
				const message = first(error.issues)?.message
				if (message) toast.error(message)
			}
			throw error
		})

		return post<{ data: Comment }>(ADD_COMMENT, payload).then((response) => {
			methods.reset()
			contentEditorRef.current?.clear()
			onSubmit?.(response.data.data)
		})
	}

	return (
		<FormProvider {...methods}>
			<AttachmentDropzone
				accept={[MIME_TYPES.png, MIME_TYPES.jpeg]}
				onDrop={attachmentHandlers.handleAttachmentsDropped}
			>
				<div className={clsx(styles.container, className)} {...props}>
					{user ? (
						<Avatar data={user} className={styles.avatar} />
					) : (
						<MantineAvatar size={42} color="gray" className={clsx('jk-avatar', styles.avatar)} />
					)}
					<form onSubmit={methods.handleSubmit(handleSubmit)} className={styles.form}>
						<CommentFormFields
							allowSyncToPersonalUpdate={allowSyncToPersonalUpdate}
							attachmentHandlers={attachmentHandlers}
							autoFocus={autoFocus}
							contentEditorRef={contentEditorRef}
							onSubmit={handleSubmit}
							placeholder={placeholder}
							submitLabel={submitLabel}
							to={replyToComment?.to}
						/>
					</form>
				</div>
			</AttachmentDropzone>
		</FormProvider>
	)
}
